import { api } from "../../lib/api";

export interface PortfolioSummary {
  securities_value: string;
  invested_cost: string;
  unrealized_pnl: string;
  realized_pnl: string;
  day_change_value: string;
  cash_balance: string;
  total_value: string;
  /** Simple total return (%): (unrealized + realized) / invested_cost. */
  total_return_pct: string | null;
  /** Money-weighted annualised return (XIRR, %) across the whole portfolio. */
  weighted_annualized_return: string | null;
}

/** `sgi` holds actions + obligations, `sgo` holds FCP only. */
export type PortfolioKind = "sgi" | "sgo";

export const PORTFOLIO_KIND_LABEL: Record<PortfolioKind, string> = {
  sgi: "SGI · actions & obligations",
  sgo: "SGO · FCP",
};

/** Security types each kind may hold — mirrors PORTFOLIO_ALLOWED_TYPES in @brvm/db. */
export const PORTFOLIO_ALLOWED_TYPES: Record<PortfolioKind, string[]> = {
  sgi: ["action", "obligation"],
  sgo: ["fcp"],
};

export interface Portfolio {
  id: string;
  name: string;
  baseCurrency: string;
  isDefault: boolean;
  kind: PortfolioKind;
  /** Target annual return, in percent — null only for legacy portfolios. */
  targetReturn: string | null;
  createdAt: string;
  summary: PortfolioSummary;
}

export interface Position {
  security_id: string;
  ticker: string;
  name: string;
  quantity: string;
  cump: string;
  total_cost: string;
  realized_pnl: string;
  market_price: string | null;
  price_date: string | null;
  market_value: string | null;
  unrealized_pnl: string | null;
  unrealized_pnl_pct: string | null;
  day_change_value: string | null;
  annualized_return: string | null;
}

export const listPortfolios = () => api<{ data: Portfolio[] }>("/portfolios").then((r) => r.data);

export const getPortfolio = (id: string) =>
  api<{ portfolio: Portfolio }>(`/portfolios/${id}`).then((r) => r.portfolio);

export interface NewPortfolioInput {
  name: string;
  kind: PortfolioKind;
  targetReturn: string;
}

export const createPortfolio = (input: NewPortfolioInput) =>
  api<{ portfolio: Portfolio }>("/portfolios", { method: "POST", body: input }).then(
    (r) => r.portfolio,
  );

export const listPositions = (portfolioId: string) =>
  api<{ data: Position[] }>(`/portfolios/${portfolioId}/positions`).then((r) => r.data);
